/*
 *
 * Copyright (c) 1999-2021 Luciad All Rights Reserved.
 *
 * Luciad grants you ("Licensee") a non-exclusive, royalty free, license to use,
 * modify and redistribute this software in source and binary code form,
 * provided that i) this copyright notice and license appear on all copies of
 * the software; and ii) Licensee does not utilize the software in a manner
 * which is disparaging to Luciad.
 *
 * This software is provided "AS IS," without a warranty of any kind. ALL
 * EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS AND WARRANTIES, INCLUDING ANY
 * IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
 * NON-INFRINGEMENT, ARE HEREBY EXCLUDED. LUCIAD AND ITS LICENSORS SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE AS A RESULT OF USING, MODIFYING
 * OR DISTRIBUTING THE SOFTWARE OR ITS DERIVATIVES. IN NO EVENT WILL LUCIAD OR ITS
 * LICENSORS BE LIABLE FOR ANY LOST REVENUE, PROFIT OR DATA, OR FOR DIRECT,
 * INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL OR PUNITIVE DAMAGES, HOWEVER
 * CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY, ARISING OUT OF THE USE OF
 * OR INABILITY TO USE SOFTWARE, EVEN IF LUCIAD HAS BEEN ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGES.
 */
import {Controller} from "@luciad/ria/view/controller/Controller";
import {EVENT_HANDLED, EVENT_IGNORED} from "@luciad/ria/view/controller/HandleEventResult";
import {GestureEventType} from "@luciad/ria/view/input/GestureEventType";
import {getReference} from "@luciad/ria/reference/ReferenceProvider";
import {createCartesianGeodesy} from "@luciad/ria/geodesy/GeodesyFactory";
import {LocationMode} from "@luciad/ria/transformation/LocationMode";
import {createTransformation} from "@luciad/ria/transformation/TransformationFactory";
import {Measurement} from "./Measurement";
import {Ruler3DPresentation} from "./Ruler3DPresentation";
import {EventedSupport} from "@luciad/ria/util/EventedSupport";
import {MeasureType} from "./MeasureType";

const SQUARED_SLACK = 100; //some slack when clicking.

export const MEASUREMENT_CHANGED_EVENT = "MEASUREMENT_CHANGED";
export const ENABLED_CHANGE_EVENT = "ENABLED_CHANGED";
export const ACTIVATED_EVENT = "ACTIVATED";
export const DEACTIVATED_EVENT = "ACTIVATED";

/**
 * Creates the Ruler Controller instance.
 * @constructor
 */
class Ruler3DController extends Controller {

  constructor(options) {
    super();
    options = options || {};
    this._measureOptions = {};

    this._wait = false;
    this._dx = null;
    this._dy = null;
    this._eventedSupport = new EventedSupport([
        MEASUREMENT_CHANGED_EVENT,
        ENABLED_CHANGE_EVENT,
        ACTIVATED_EVENT,
        DEACTIVATED_EVENT
    ], true);
    this._enabled = typeof options.enabled !== "undefined" ? options.enabled : true;

    options = options || {};
    this.setMeasureType(typeof options.measureType !== "undefined" ? options.measureType : MeasureType.SIMPLE);
  }

  _init(map) {
    const worldReference = map.reference; // "EPSG:4978"
    const shapeReference = getReference("EPSG:4326"); // 3D WGS:84

    const geoContext = {
      worldReference: worldReference,
      shapeReference: shapeReference,
      geodesy: createCartesianGeodesy(worldReference),
      worldToModelTx: createTransformation(worldReference, shapeReference),
      modelToWorldTx: createTransformation(shapeReference, worldReference),
      viewToWorldTx: map.getViewToMapTransformation(LocationMode.CLOSEST_SURFACE)
    };

    this._measurement = new Measurement(geoContext);
    this._presentation = Ruler3DPresentation.createPresentation(geoContext);
  }

  on(event, callback) {
    if (
        event === MEASUREMENT_CHANGED_EVENT ||
        event === ENABLED_CHANGE_EVENT ||
        event === ACTIVATED_EVENT ||
        event === DEACTIVATED_EVENT
    ) {
      return this._eventedSupport.on(event, callback);
    }
    return super.on(event, callback);
  }

  /**
   * Called when the controller becomes active. Perform any setup here.
   */
  onActivate(map) {
    this._init(map);
    this._eventedSupport.emit(ACTIVATED_EVENT);
    super.onActivate(map);
  }

  /**
   * called when the controller becomes inactive
   * perform any cleanup here.
   */
  onDeactivate(map) {
    this._measurement.reset();
    this._eventedSupport.emit(DEACTIVATED_EVENT);
    return super.onDeactivate(map);
  }

  get enabled() {
    return this._enabled;
  }

  set enabled(enabled) {
    if (this._enabled !== enabled) {
      this._enabled = enabled;
      this._measurement.reset();
      this.invalidate();
      this._eventedSupport.emit(ENABLED_CHANGE_EVENT, enabled);
    }
  }

  _letGo() {
    this._wait = false;
    this._dx = undefined;
    this._dy = undefined;
  }

  _withinSlack(event) {
    if (!this._wait) {
      return false;
    }

    const x = event.viewPosition[0];
    const y = event.viewPosition[1];
    const dist = squaredDistance(this._dx, this._dy, x, y);
    return (dist < SQUARED_SLACK);

    function squaredDistance(x1, y1, x2, y2) {
      return Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2);
    }
  }

  _onDrag(event) {
    if (this._wait) {
      if (this._withinSlack(event)) {
        return EVENT_HANDLED;
      }
    } else {
      this._letGo();
    }
  }

  _onDragEnd(event) {
    if (this._withinSlack(event)) {
      return this._onClickConfirmed(event);
    }
    this._letGo();
  }

  _onMove(event) {
    if (!this._measurement.isStarted()) {
      return;
    }
    this._letGo();

    this._measurement.updateLastPosition(event.viewPosition);
    this.invalidate();
    return EVENT_HANDLED;
  }

  _onClick(event) {
    this._measurement.addNewPosition(event.viewPosition);
    this._measurement.start(event.viewPosition);
    this.invalidate();
    return EVENT_HANDLED;
  }

  _onDown(event) {
    //by holding on to the down coordinates, we can introduce a little slack
    const loc = event.viewPosition;
    this._dx = loc[0];
    this._dy = loc[1];
    this._wait = true;
    return EVENT_HANDLED;
  }

  _onDoubleClick() /*event*/{
    this._measurement.stop();
    this.invalidate();
    return EVENT_HANDLED;
  }

  /**
   * Handle the user input gestures. The event-object contains information about the type of user-interaction
   */
  onGestureEvent(event) {
    if (!this._enabled) {
      return EVENT_IGNORED;
    }
    switch (event.type) {
    case GestureEventType.DRAG:
      return this._onDrag(event);
    case GestureEventType.DOWN:
      return this._onDown(event);
    case GestureEventType.DRAG_END:
      return this._onDragEnd(event);
    case GestureEventType.MOVE:
      //when user "hovers" over the map, we measure, but do not confirm any point
      return this._onMove(event);
    case GestureEventType.DOUBLE_CLICK:
      //user confirms the polyline
      return this._onDoubleClick(event);
    case GestureEventType.SINGLE_CLICK_UP:
      //user performed click and no double click can follow
      return this._onClick(event);
    default:
      break;
    }
  }

  setMeasureType(measureType) {
    this._measureOptions.measureType = measureType;
    this.invalidate();
  }

  isCurrentMeasureType(measureType) {
    return this._measureOptions.measureType === measureType;
  }

  /**
   * This method is invoked whenever the controller is invalidated.
   */
  onDraw(geoCanvas) {
    if (!this._enabled) {
      return;
    }
    // Array of {line, distance, p1, p2} where p1, p2 {modelPoint, worldPoint}
    const segments = this._measurement.segments;

    const isTypeOrtho = this.isCurrentMeasureType(MeasureType.ORTHO);
    const isTypeHeight = this.isCurrentMeasureType(MeasureType.HEIGHT);
    const isTypeArea = this.isCurrentMeasureType(MeasureType.AREA);

    if (isTypeArea) {
      this._presentation.drawArea(geoCanvas, segments);
    }

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      this._presentation.drawSegmentPoints(geoCanvas, segment, i);

      if (!isTypeHeight) {
        this._presentation.drawSegment(geoCanvas, segment);
      }

      if (isTypeOrtho) {
        this._presentation.drawOrtho(geoCanvas, segment);
      }

      if (isTypeHeight) {
        this._presentation.drawHeight(geoCanvas, segment, i);
      }
    }
    // Controller implements Evented - inform listeners that measure results changed
    this._eventedSupport.emit(MEASUREMENT_CHANGED_EVENT);
  }

  onDrawLabel(labelCanvas) {
    if (!this._enabled) {
      return;
    }
    // Array of {line, distance, p1, p2} where p1, p2 {modelPoint, worldPoint}
    const segments = this._measurement.segments;

    const isTypeOrtho = this.isCurrentMeasureType(MeasureType.ORTHO);
    const isTypeHeight = this.isCurrentMeasureType(MeasureType.HEIGHT);
    const isTypeArea = this.isCurrentMeasureType(MeasureType.AREA);

    if (isTypeArea) {
      this._presentation.drawAreaLabel(labelCanvas, segments);
    }

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      if (!isTypeHeight) {
        this._presentation.drawSegmentLabel(labelCanvas, segment);
      }

      if (isTypeOrtho) {
        this._presentation.drawOrthoLabel(labelCanvas, segment);
      }

      if (isTypeHeight) {
        this._presentation.drawHeightLabel(labelCanvas, segment, i);
      }
    }
  }

  getMeasurements() {
    const segments = this._measurement.segments;
    return {
      segments: segments,
      totals: this._presentation.getTotals(segments)
    };
  }
}

export {Ruler3DController};

